import React from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { hapticEffects } from '@aytm/app/swidget/base/lib/hapticEffects'
import { IEcommerceQuestionSchema } from '../../types'
import style from './Footer.module.scss'
import commonStyle from '../../style.module.scss'

interface IFooterProps {
  showCart: boolean
  totals: number[]
  schema: IEcommerceQuestionSchema
  findability: boolean
  renderError: () => React.ReactNode
  checkout: () => void
  onCartOpen: () => void
  onHomeClick: () => void
  showHint: () => void
  closeSimulation: () => void
}

const Footer = ({
  showCart,
  totals,
  schema,
  findability,
  renderError,
  checkout,
  onCartOpen,
  onHomeClick,
  showHint,
  closeSimulation
}: IFooterProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  const valid =
    (schema.max_money_spent ? totals[1] <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? totals[0] <= schema.max_cart_size : true)
  return (
    <>
      {viewport.mobile ? (
        <footer className={style.footerMobile}>
          {showCart && (
            <>
              {renderError()}
              {totals[0] > 0 ? (
                <button
                  className={`${style.checkout} ${!valid ? style.disabled : ''}`}
                  onClick={() => {
                    hapticEffects.selection()
                    checkout()
                  }}
                >
                  {t('questions', 'ec', 'ecommerce', 'checkout')}
                </button>
              ) : (
                <button className={style.checkout} onClick={onHomeClick}>
                  {t('questions', 'ec', 'ecommerce', 'shopNow')}
                </button>
              )}
            </>
          )}
          <div className={style.nav}>
            <button className={style.footerItem} onClick={onHomeClick}>
              <div className={style.homeIcon} />
              <span className={style.itemText}>{t('questions', 'ec', 'ecommerce', 'home')}</span>
            </button>
            <button className={style.footerItem} onClick={onHomeClick}>
              <div className={style.shopIcon} />
              <span className={style.itemText}>{t('questions', 'ec', 'ecommerce', 'shop')}</span>
            </button>
            {findability ? (
              <EmptyLink color="#111">
                <div className={style.footerItem}>
                  <div className={style.bagIcon} />
                  <span className={style.itemText}>{t('questions', 'ec', 'ecommerce', 'cart')}</span>
                </div>
              </EmptyLink>
            ) : (
              <button className={style.footerItem} onClick={onCartOpen}>
                <div className={style.bagIcon}>
                  <div className={style.amount}>{totals[0] > 99 ? '99+' : totals[0]}</div>
                </div>
                <span className={style.itemText}>{t('questions', 'ec', 'ecommerce', 'cart')}</span>
              </button>
            )}
            <div className={`${style.footerItem} ${style.scaled}`}>
              <div className={`${commonStyle.IconAytmPlace}`}>
                <i className=" icon-aytmfd-help" onClick={showHint} />
              </div>
            </div>
            <div className={`${style.footerItem} ${style.scaled}`}>
              <div className={`${commonStyle.IconAytmPlace}`}>
                <i className=" icon-aytmfd-exit" onClick={closeSimulation} />
              </div>
            </div>
          </div>
        </footer>
      ) : (
        <footer className={style.footer}>
          <div className={style.links}>
            <div className={style.column}>
              <div className={style.title}>{t('questions', 'ec', 'ecommerce', 'resourses')}</div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'giftCards')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'findStore')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'membership')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">
                  {schema.name} {t('questions', 'ec', 'ecommerce', 'journal')}
                </EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'siteFeedback')}</EmptyLink>
              </div>
            </div>
            <div className={style.column}>
              <div className={style.title}>{t('questions', 'ec', 'ecommerce', 'help')}</div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'getHelp')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'orderStatus')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'shipAndDelivery')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'returns')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'orderCancel')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'paymentOptions')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'giftBalance')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'contact')}</EmptyLink>
              </div>
            </div>
            <div className={style.column}>
              <div className={style.title}>{t('questions', 'ec', 'ecommerce', 'company')}</div>
              <div className={style.link}>
                <EmptyLink color="#111">
                  {t('questions', 'ec', 'ecommerce', 'about')} {schema.name}
                </EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'news')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'careers')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'investors')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'purpose')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'sustainability')}</EmptyLink>
              </div>
            </div>
            <div className={style.column}>
              <div className={style.title}>{t('questions', 'ec', 'ecommerce', 'promosAndDiscounts')}</div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'student')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'military')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'teacher')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'professional')}</EmptyLink>
              </div>
              <div className={style.link}>
                <EmptyLink color="#111">{t('questions', 'ec', 'ecommerce', 'birthday')}</EmptyLink>
              </div>
            </div>
          </div>
          <div className={style.privacy}>
            <EmptyLink color="#111">
              <div className={style.item}>
                {t<(store: string) => string>('questions', 'ec', 'ecommerce', 'allRights')(schema.name)}
              </div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>
                {t('questions', 'ec', 'ecommerce', 'guides')}
                <div className={style.downIcon} />
              </div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>{t('questions', 'ec', 'ecommerce', 'termsOfSale')}</div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>{t('questions', 'ec', 'ecommerce', 'termsOfUse')}</div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>
                {schema.name}&nbsp;{t('questions', 'ec', 'ecommerce', 'privacy')}
              </div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>
                <div className={style.image} />
                {t('questions', 'ec', 'ecommerce', 'privacyChoice')}
              </div>
            </EmptyLink>
            <EmptyLink color="#111">
              <div className={style.item}>{t('questions', 'ec', 'ecommerce', 'supplyChains')}</div>
            </EmptyLink>
          </div>
        </footer>
      )}
    </>
  )
}
export default Footer
